import {
  LoginCredentials,
  RegistrationCredentials,
  OrganizationCredentials,
  LoginResponse,
  AuthUser,
} from './types';

import { AUTH_STORAGE_KEYS, API_ENDPOINTS, HTTP_HEADERS, CONTENT_TYPES } from './constants';
import { ApiClient } from '@utils/apiClient';
import { CheckoutSessionResponse } from '@pages/Billing/types';
import i18n from '@i18n';

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await ApiClient.post(API_ENDPOINTS.LOGIN, credentials);

    if (!response.ok) {
      // Handle 403 Forbidden with payment_required flag
      if (response.status === 403) {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.payment_required) {
          // Return response with payment_required flag but don't store tokens
          return {
            ...errorData,
            payment_required: true,
          } as LoginResponse;
        }
      }
      const errorData = await response.json().catch(() => ({ detail: i18n.t('auth.loginFailed') }));
      throw new Error(errorData.detail || errorData.message || i18n.t('auth.loginFailed'));
    }

    const result = await response.json();

    // Only store tokens if payment is not required (organization is active)
    if (!result.payment_required) {
      if (result.access) {
        localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, result.access);
      }
      if (result.refresh) {
        localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, result.refresh);
      }
      if (result.organization_id) {
        localStorage.setItem(AUTH_STORAGE_KEYS.ORGANIZATION_ID, result.organization_id);
      }
    }

    return result;
  },

  async register(credentials: RegistrationCredentials): Promise<LoginResponse> {
    const response = await ApiClient.post(API_ENDPOINTS.REGISTER, credentials);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: i18n.t('auth.registrationFailed') }));

      // Handle detailed field errors from backend
      if (errorData.password1) {
        throw new Error(
          Array.isArray(errorData.password1) ? errorData.password1[0] : errorData.password1
        );
      }
      if (errorData.email) {
        throw new Error(Array.isArray(errorData.email) ? errorData.email[0] : errorData.email);
      }
      if (errorData.first_name) {
        throw new Error(
          Array.isArray(errorData.first_name) ? errorData.first_name[0] : errorData.first_name
        );
      }
      if (errorData.last_name) {
        throw new Error(
          Array.isArray(errorData.last_name) ? errorData.last_name[0] : errorData.last_name
        );
      }
      if (errorData.accepted_terms) {
        throw new Error(
          Array.isArray(errorData.accepted_terms)
            ? errorData.accepted_terms[0]
            : errorData.accepted_terms
        );
      }
      if (errorData.non_field_errors) {
        throw new Error(
          Array.isArray(errorData.non_field_errors)
            ? errorData.non_field_errors[0]
            : errorData.non_field_errors
        );
      }

      throw new Error(errorData.detail || errorData.message || i18n.t('auth.registrationFailed'));
    }

    const result = await response.json();

    if (result.access) {
      localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, result.access);
    }
    if (result.refresh) {
      localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, result.refresh);
    }

    return result;
  },

  async verifyEmailCode(code: string): Promise<void> {
    const response = await ApiClient.post(API_ENDPOINTS.VERIFY_EMAIL, { code });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: i18n.t('auth.emailVerificationFailed') }));
      throw new Error(
        errorData.detail || errorData.message || i18n.t('auth.emailVerificationFailed')
      );
    }
  },

  async resendEmailVerification(email: string): Promise<void> {
    const response = await ApiClient.post(API_ENDPOINTS.RESEND_EMAIL, { email });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: i18n.t('auth.resendVerificationFailed') }));
      throw new Error(
        errorData.detail || errorData.message || i18n.t('auth.resendVerificationFailed')
      );
    }
  },

  async resetPassword(email: string): Promise<void> {
    const response = await ApiClient.post(API_ENDPOINTS.PASSWORD_RESET, { email });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || i18n.t('auth.passwordResetRequestFailed'));
    }
  },

  async confirmPasswordReset(data: {
    uid: string;
    token: string;
    new_password1: string;
    new_password2: string;
  }): Promise<void> {
    const response = await ApiClient.post(API_ENDPOINTS.PASSWORD_RESET_CONFIRM, data);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || i18n.t('auth.passwordResetConfirmFailed'));
    }
  },

  async refreshToken(refreshToken: string): Promise<{ access: string }> {
    const response = await ApiClient.post(API_ENDPOINTS.REFRESH, { refresh: refreshToken });

    if (!response.ok) {
      throw new Error(i18n.t('auth.tokenRefreshFailed'));
    }

    const result = await response.json();

    if (result.access) {
      localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, result.access);
    }

    return result;
  },

  async logout(): Promise<void> {
    try {
      await ApiClient.post(API_ENDPOINTS.LOGOUT);
    } catch (error) {
      // Ignore logout errors
    } finally {
      this.clearTokens();
    }
  },

  async submitSepaMandate(
    organizationId: string,
    data: { account_holder: string; iban: string; consent: boolean },
    accessToken?: string
  ): Promise<{ status: string; iban_last4: string; mandate_reference: string }> {
    if (!accessToken?.trim()) {
      throw new Error(i18n.t('auth.accessTokenRequired'));
    }

    const originalToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken.trim());

    try {
      const response = await ApiClient.post(API_ENDPOINTS.SEPA_MANDATE, {
        organization_id: organizationId,
        ...data,
      });

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: i18n.t('auth.organizationCreationFailed') }));
        const error = new Error(
          errorData.detail || errorData.message || i18n.t('auth.organizationCreationFailed')
        );
        (error as Error & { fieldErrors?: Record<string, unknown> }).fieldErrors =
          errorData.field_errors || errorData;
        throw error;
      }

      return await response.json();
    } finally {
      if (originalToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, originalToken);
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
      }
    }
  },

  async createOrganization(
    credentials: OrganizationCredentials,
    accessToken?: string
  ): Promise<{ id: string; [key: string]: unknown }> {
    if (!accessToken?.trim()) {
      throw new Error(i18n.t('auth.accessTokenRequired'));
    }

    const originalToken = localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken.trim());

    try {
      const response = await ApiClient.post(API_ENDPOINTS.CREATE_ORGANIZATION, credentials);

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ detail: i18n.t('auth.organizationCreationFailed') }));

        const error = new Error(
          errorData.detail || errorData.message || i18n.t('auth.organizationCreationFailed')
        );

        if (errorData.field_errors) {
          (error as Error & { fieldErrors?: Record<string, string[]> }).fieldErrors =
            errorData.field_errors;
        }

        throw error;
      }

      const result = await response.json();

      if (result.id) {
        localStorage.setItem(AUTH_STORAGE_KEYS.ORGANIZATION_ID, result.id);
      }

      return result;
    } finally {
      if (originalToken) {
        localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, originalToken);
      } else {
        localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
      }
    }
  },

  /**
   * Creates a Stripe checkout session for registration payment.
   *
   * Backend Requirements:
   * - Must include organization_id in payment intent metadata for webhook processing
   * - Payment intent metadata should contain: { organization_id: "<uuid>" }
   * - This ensures webhook handlers can identify the organization without querying checkout session
   *
   * @param organizationId - The organization UUID
   * @param couponCode - Optional coupon code for discount
   * @param savePaymentMethod - Whether to save the payment method for future use
   * @returns CheckoutSessionResponse with checkout_url and session_id
   */
  async createRegistrationPayment(
    organizationId: string,
    couponCode?: string,
    baseUrl?: string
  ): Promise<CheckoutSessionResponse> {
    if (!organizationId?.trim()) {
      throw new Error(i18n.t('auth.orgIdRequired'));
    }

    const requestBody: {
      organization_id: string;
      coupon_code?: string;
      base_url?: string;
    } = {
      organization_id: organizationId.trim(),
    };

    if (baseUrl) {
      requestBody.base_url = baseUrl;
    }

    // Only include coupon_code if it's provided and not empty
    if (couponCode && couponCode.trim()) {
      requestBody.coupon_code = couponCode.trim();
    }

    const response = await ApiClient.post(API_ENDPOINTS.COMPLETE_REGISTRATION_PAYMENT, requestBody);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ detail: i18n.t('auth.checkoutSessionFailed') }));
      throw new Error(
        errorData.detail || errorData.message || i18n.t('auth.checkoutSessionFailed')
      );
    }

    const result: CheckoutSessionResponse = await response.json();
    return result;
  },

  getAccessToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  },

  getStoredToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  },

  getStoredRefreshToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  },

  getStoredOrganizationId(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ORGANIZATION_ID);
  },

  setTokens(accessToken: string, refreshToken: string): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  },

  async getCurrentUser(): Promise<AuthUser> {
    const response = await ApiClient.get(API_ENDPOINTS.CURRENT_USER);

    if (!response.ok) {
      throw new Error(i18n.t('auth.fetchCurrentUserFailed'));
    }

    const result = await response.json();
    return result;
  },

  clearTokens(): void {
    Object.values(AUTH_STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  },

  getAuthHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      [HTTP_HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
    };

    const token = this.getStoredToken();
    if (token) {
      headers[HTTP_HEADERS.AUTHORIZATION] = `Bearer ${token}`;
    }

    const orgId = this.getStoredOrganizationId();
    if (orgId?.trim()) {
      headers[HTTP_HEADERS.ORGANIZATION_ID] = orgId;
    }

    return headers;
  },
};
